import { Link } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { useBookCall } from "../components/BookCallDialog";
import { useSeo } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  ArrowUpRight, ShieldCheck, Languages, Zap, Sparkles, Calendar,
  CheckCircle2, Banknote, Trash2, Boxes,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1772338537689-056082f100a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxtYW4lMjBhc3NlbWJsaW5nJTIwZnVybml0dXJlJTIwaG9tZXxlbnwwfHx8fDE3Nzc4MjA2NDZ8MA&ixlib=rb-4.1.0&q=85";

const CONTENT = {
  en: {
    title: "IKEA Assembly Service Munich | MUConcierge",
    description: "Professional IKEA furniture assembly service in Munich. PAX, MALM, BEKANT, BRIMNES — English-speaking team, fast response, fixed transparent pricing. Book your IKEA put-together service today.",
    tagline: "Munich · IKEA assembly · English-speaking",
    h1Top: "IKEA furniture assembly",
    h1Bottom: "in Munich.",
    sub: "Professional IKEA furniture assembly service for English-speaking expats and Munich residents. We put together your PAX, MALM, BEKANT and BILLY — perfectly, quickly, insured.",
    cta1: "Book IKEA assembly",
    cta2: "Get a quote",
    trust1: "Fixed pricing",
    trust2: "English-speaking",
    trust3: "Weekend slots",
    catsTitle: "We assemble every IKEA system you own.",
    catsLead: "From a single BILLY bookshelf to a wall-to-wall PAX wardrobe — our Munich-based assembly team knows IKEA inside out.",
    cats: [
      { name: "Wardrobes", items: ["PAX", "PLATSA", "BRIMNES"] },
      { name: "Bedroom", items: ["MALM beds", "HEMNES", "SONGESAND"] },
      { name: "Office", items: ["BEKANT", "MICKE", "ALEX desks"] },
    ],
    whyTitle: "Why customers choose us",
    whyLead: "An IKEA furniture assembly service in Munich built for expats — and anyone who'd rather skip the Allen key marathon.",
    whyItems: [
      { icon: Languages, t: "English-speaking team", d: "Every assembler, every email, every invoice — in English." },
      { icon: Zap, t: "Fast response", d: "We reply within 4 business hours and can book same-week slots." },
      { icon: Banknote, t: "Fixed transparent pricing", d: "Quote upfront, no hidden charges, no surprises on the day." },
      { icon: Sparkles, t: "Clean and careful work", d: "Floors covered, dust managed, finger-prints wiped before we leave." },
      { icon: Boxes, t: "Experience with IKEA systems", d: "PAX, BESTÅ, BILLY, MALM, BEKANT — we've assembled them hundreds of times." },
      { icon: Calendar, t: "Weekend appointments", d: "Saturdays bookable — your work week stays untouched." },
      { icon: ShieldCheck, t: "Insured service", d: "Fully insured for property and worker liability." },
      { icon: Trash2, t: "Packaging disposal", d: "Every box flattened and recycled per Munich rules. You keep zero cardboard." },
    ],
    flowTitle: "How IKEA assembly with us works",
    steps: [
      { n: "01", t: "Tell us what you bought", d: "Send your IKEA order list or product names. We'll quote within 4 hours." },
      { n: "02", t: "Pick a slot", d: "Weekday or weekend, we work around your move-in date." },
      { n: "03", t: "We assemble", d: "Two-person team arrives on time, with the right tools, in your apartment." },
      { n: "04", t: "Done & cleaned", d: "Furniture in place, boxes gone, walkthrough complete." },
    ],
    pricingTitle: "Fixed IKEA assembly pricing",
    pricingNote: "Single-room IKEA assembly starts from €450. Full apartment IKEA setup starts from €1,290. Custom quote for larger or premium jobs.",
    faqTitle: "IKEA assembly — frequently asked",
    faqs: [
      { q: "Do you assemble all IKEA furniture?", a: "Yes — wardrobes (PAX, PLATSA, BRIMNES), beds (MALM, HEMNES, SONGESAND), desks (BEKANT, MICKE, ALEX), kitchen units (METOD), BILLY bookcases, BESTÅ systems, and most other ranges." },
      { q: "How much does IKEA assembly in Munich cost?", a: "Single-room IKEA assembly starts from €450. We quote each job upfront so you know the full price before we begin — no hourly billing surprises." },
      { q: "Can you pick up my IKEA order?", a: "Yes — we coordinate delivery from IKEA München-Brunnthal or IKEA Eching, or receive your IKEA home delivery if you prefer." },
      { q: "Do you offer weekend appointments?", a: "Yes. Saturday slots are available. Sunday on request for time-critical move-ins." },
      { q: "What if a part is missing or damaged?", a: "We handle return runs and exchange logistics for IKEA. The first replacement run within 5 km is included." },
      { q: "Do you also assemble non-IKEA furniture?", a: "Yes — see our M and L packages on the homepage for full apartment setup, premium brands, and ongoing post-move support." },
    ],
    backToHome: "← Back to MUConcierge home",
    finalCta: "Get your IKEA furniture assembled in Munich",
    finalCtaSub: "Insured, English-speaking, transparent pricing. We'll reply within 4 business hours.",
    finalCtaBtn: "Book IKEA assembly",
  },
  de: {
    title: "IKEA Möbelmontage München | IKEA Aufbauservice — MUConcierge",
    description: "Professionelle IKEA Möbelmontage in München. PAX, MALM, BEKANT, BRIMNES — englischsprachiges Team, schnelle Reaktion, faire Festpreise. IKEA Aufbauservice München jetzt buchen.",
    tagline: "München · IKEA Möbelmontage · Englisch- & Deutschsprachig",
    h1Top: "IKEA Möbelmontage",
    h1Bottom: "in München.",
    sub: "Professioneller IKEA Aufbauservice in München für Expats und Einheimische. Wir montieren Ihre PAX, MALM, BEKANT und BILLY — präzise, schnell und versichert.",
    cta1: "IKEA Montage buchen",
    cta2: "Angebot anfragen",
    trust1: "Festpreise",
    trust2: "Englisch & Deutsch",
    trust3: "Wochenend-Termine",
    catsTitle: "Wir bauen jedes IKEA-System auf, das Sie haben.",
    catsLead: "Vom einzelnen BILLY-Regal bis zum wandhohen PAX-Kleiderschrank — unser Münchner Montageteam kennt IKEA-Systeme in- und auswendig.",
    cats: [
      { name: "Kleiderschränke", items: ["PAX", "PLATSA", "BRIMNES"] },
      { name: "Schlafzimmer", items: ["MALM Betten", "HEMNES", "SONGESAND"] },
      { name: "Büro", items: ["BEKANT", "MICKE", "ALEX Schreibtische"] },
    ],
    whyTitle: "Warum Kunden uns wählen",
    whyLead: "Ein IKEA Möbelmontage-Service in München, der für Expats — und alle, die sich den Inbusschlüssel-Marathon sparen möchten — gemacht ist.",
    whyItems: [
      { icon: Languages, t: "Englischsprachiges Team", d: "Jeder Monteur, jede E-Mail, jede Rechnung — auf Englisch und Deutsch." },
      { icon: Zap, t: "Schnelle Reaktion", d: "Antwort innerhalb von 4 Werktagsstunden, Termine oft noch in derselben Woche." },
      { icon: Banknote, t: "Faire Festpreise", d: "Angebot im Voraus, keine versteckten Kosten, keine Überraschungen am Aufbautag." },
      { icon: Sparkles, t: "Sauberes und sorgfältiges Arbeiten", d: "Böden abgedeckt, Staub kontrolliert, Fingerabdrücke entfernt bevor wir gehen." },
      { icon: Boxes, t: "Erfahrung mit IKEA-Systemen", d: "PAX, BESTÅ, BILLY, MALM, BEKANT — wir haben sie hunderte Male aufgebaut." },
      { icon: Calendar, t: "Wochenend-Termine", d: "Samstage buchbar — Ihre Arbeitswoche bleibt unangetastet." },
      { icon: ShieldCheck, t: "Versicherter Service", d: "Vollständig versichert für Sach- und Personenschäden." },
      { icon: Trash2, t: "Verpackungsentsorgung", d: "Jeder Karton zerlegt und nach Münchner Regeln entsorgt. Sie behalten null Karton." },
    ],
    flowTitle: "So funktioniert die IKEA Möbelmontage bei uns",
    steps: [
      { n: "01", t: "Sagen Sie uns, was Sie gekauft haben", d: "Senden Sie Ihre IKEA-Bestellliste oder Produktnamen. Angebot innerhalb von 4 Stunden." },
      { n: "02", t: "Termin wählen", d: "Werktag oder Wochenende — wir richten uns nach Ihrem Einzugstermin." },
      { n: "03", t: "Wir bauen auf", d: "Zwei-Personen-Team kommt pünktlich, mit dem richtigen Werkzeug, in Ihre Wohnung." },
      { n: "04", t: "Fertig & sauber", d: "Möbel an Ort und Stelle, Kartons weg, Abschluss-Begehung erledigt." },
    ],
    pricingTitle: "Faire IKEA Montage-Festpreise",
    pricingNote: "IKEA Möbelmontage für ein Zimmer ab €450. Komplette IKEA Wohnungseinrichtung ab €1.290. Individuelles Angebot für größere oder Premium-Aufträge.",
    faqTitle: "IKEA Möbelmontage — häufige Fragen",
    faqs: [
      { q: "Montieren Sie alle IKEA-Möbel?", a: "Ja — Kleiderschränke (PAX, PLATSA, BRIMNES), Betten (MALM, HEMNES, SONGESAND), Schreibtische (BEKANT, MICKE, ALEX), Küchen (METOD), BILLY-Regale, BESTÅ-Systeme und die meisten anderen Serien." },
      { q: "Was kostet die IKEA Möbelmontage in München?", a: "Eine IKEA-Montage für ein Zimmer beginnt ab €450. Wir kalkulieren jeden Auftrag im Voraus — keine Überraschungen durch Stundenabrechnung." },
      { q: "Können Sie meine IKEA-Bestellung abholen?", a: "Ja — wir koordinieren die Lieferung von IKEA München-Brunnthal oder IKEA Eching oder nehmen Ihre IKEA-Lieferung in Empfang." },
      { q: "Bieten Sie Wochenend-Termine an?", a: "Ja. Samstagstermine sind buchbar. Sonntag auf Anfrage für zeitkritische Einzüge." },
      { q: "Was, wenn ein Teil fehlt oder beschädigt ist?", a: "Wir übernehmen Rückgaben und Umtausch bei IKEA. Die erste Ersatzfahrt innerhalb von 5 km ist inklusive." },
      { q: "Montieren Sie auch Möbel anderer Marken?", a: "Ja — siehe unsere M- und L-Pakete auf der Startseite für komplette Wohnungseinrichtung, Premiummarken und laufende After-Move-Unterstützung." },
    ],
    backToHome: "← Zurück zur MUConcierge-Startseite",
    finalCta: "IKEA Möbel in München aufbauen lassen",
    finalCtaSub: "Versichert, englisch- & deutschsprachig, transparente Preise. Antwort innerhalb von 4 Werktagsstunden.",
    finalCtaBtn: "IKEA Montage buchen",
  },
};

export default function IkeaPage({ lang = "en" }) {
  const t = CONTENT[lang];
  const { openDialog } = useBookCall();
  const homePath = lang === "de" ? "/de" : "/";

  useSeo({
    title: t.title,
    description: t.description,
    path: lang === "de" ? "/ikea-moebelmontage-muenchen" : "/ikea-assembly-munich",
    lang,
    alternates: {
      en: "/ikea-assembly-munich",
      de: "/ikea-moebelmontage-muenchen",
    },
  });

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SiteHeader />

      {/* HERO */}
      <section className="relative" data-testid="ikea-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="IKEA furniture assembly Munich" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32 lg:py-40">
          <div className="max-w-3xl fade-up">
            <p className="tag-line text-[#FAF9F6]/80 mb-6">{t.tagline}</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#FAF9F6] tracking-tighter leading-[1.05]">
              {t.h1Top}
              <span className="block italic font-light text-[#E8C9B6]">{t.h1Bottom}</span>
            </h1>
            <p className="mt-8 text-lg text-[#FAF9F6]/85 max-w-xl leading-relaxed font-light">{t.sub}</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={() => openDialog({ source: `ikea-${lang}-hero`, service: lang === "de" ? "S — IKEA Möbelmontage" : "S — IKEA Assembly" })}
                className="rounded-sm bg-[#FAF9F6] text-[#1C3326] hover:bg-[#E8C9B6] text-base px-8 py-6"
                data-testid="ikea-cta-primary"
              >
                {t.cta1}
                <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => openDialog({ source: `ikea-${lang}-quote`, service: lang === "de" ? "IKEA Möbelmontage Angebot" : "IKEA Assembly quote" })}
                className="rounded-sm bg-transparent border-[#FAF9F6]/40 text-[#FAF9F6] hover:bg-[#FAF9F6]/10 hover:text-[#FAF9F6] text-base px-8 py-6"
                data-testid="ikea-cta-secondary"
              >
                {t.cta2}
              </Button>
            </div>
            <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-sm text-[#FAF9F6]/80">
              <div className="flex items-center gap-2"><Banknote className="w-4 h-4" strokeWidth={1.5} /> {t.trust1}</div>
              <div className="flex items-center gap-2"><Languages className="w-4 h-4" strokeWidth={1.5} /> {t.trust2}</div>
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4" strokeWidth={1.5} /> {t.trust3}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32" data-testid="ikea-categories">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-14">
          <div className="md:col-span-7">
            <p className="tag-line text-[#C26D5C] mb-4">IKEA · Munich</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05]">
              {t.catsTitle}
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-[#5C635A] leading-relaxed font-light">{t.catsLead}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {t.cats.map((cat) => (
            <div key={cat.name} className="bg-white border border-[#E5E2DC] p-8" data-testid={`ikea-cat-${cat.name.toLowerCase()}`}>
              <p className="tag-line text-[#C26D5C] mb-2">{cat.name}</p>
              <ul className="space-y-2 mt-4">
                {cat.items.map((it) => (
                  <li key={it} className="flex items-center gap-3 font-serif text-2xl text-[#1A201C]">
                    <span className="w-1.5 h-1.5 bg-[#1C3326]" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#F5F3EF] border-y border-[#E5E2DC]" data-testid="ikea-why">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-7">
              <p className="tag-line text-[#C26D5C] mb-4">{lang === "de" ? "Vorteile für Kunden" : "Customer benefits"}</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05]">
                {t.whyTitle}
              </h2>
              <p className="mt-5 text-[#5C635A] leading-relaxed font-light max-w-xl">{t.whyLead}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E2DC]">
            {t.whyItems.map(({ icon: Icon, t: title, d }) => (
              <div key={title} className="bg-[#FAF9F6] p-6" data-testid={`ikea-why-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <Icon className="w-5 h-5 text-[#1C3326] mb-4" strokeWidth={1.5} />
                <p className="font-serif text-lg text-[#1A201C] mb-2 leading-tight">{title}</p>
                <p className="text-[#5C635A] text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#1C3326] text-[#FAF9F6]" data-testid="ikea-flow">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-7">
              <p className="tag-line text-[#E8C9B6] mb-4">{lang === "de" ? "Ablauf" : "Process"}</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]">{t.flowTitle}</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#FAF9F6]/15">
            {t.steps.map((s) => (
              <div key={s.n} className="bg-[#1C3326] p-7 md:p-8" data-testid={`ikea-step-${s.n}`}>
                <div className="font-serif text-4xl text-[#E8C9B6] mb-5">{s.n}</div>
                <h3 className="font-serif text-xl mb-3">{s.t}</h3>
                <p className="text-[#FAF9F6]/70 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 py-20 text-center" data-testid="ikea-pricing">
        <p className="tag-line text-[#C26D5C] mb-3">{lang === "de" ? "Preise" : "Pricing"}</p>
        <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05] mb-6">{t.pricingTitle}</h2>
        <p className="text-[#5C635A] leading-relaxed font-light max-w-2xl mx-auto">{t.pricingNote}</p>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 py-16 md:py-24" data-testid="ikea-faq">
        <p className="tag-line text-[#C26D5C] mb-4 text-center">FAQ</p>
        <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight text-center mb-14">{t.faqTitle}</h2>
        <Accordion type="single" collapsible className="space-y-2">
          {t.faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border border-[#E5E2DC] bg-white px-6">
              <AccordionTrigger className="font-serif text-lg text-[#1A201C] hover:no-underline text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#5C635A] leading-relaxed font-light">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#1C3326]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 py-20 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#FAF9F6] tracking-tight leading-[1.05]">
            {t.finalCta}
          </h2>
          <p className="mt-6 text-[#FAF9F6]/80 max-w-xl mx-auto font-light">{t.finalCtaSub}</p>
          <Button
            size="lg"
            onClick={() => openDialog({ source: `ikea-${lang}-final`, service: lang === "de" ? "S — IKEA Möbelmontage" : "S — IKEA Assembly" })}
            className="mt-10 rounded-sm bg-[#FAF9F6] text-[#1C3326] hover:bg-[#E8C9B6] px-10 py-6 text-base"
            data-testid="ikea-cta-final"
          >
            {t.finalCtaBtn}
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
          <div className="mt-8">
            <Link to={homePath} className="text-xs text-[#FAF9F6]/60 underline underline-offset-4 hover:text-[#E8C9B6]">
              {t.backToHome}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
